// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// 🔴 2026-10-26 · D-101 · NAVEGAR NA FITA — «Ir para…», meses anteriores e seguintes
// ============================================================================
//
// 📗 Decisão do cliente (D-101, 2026-10-22): a fita abre 3 meses para trás e 15 para a frente;
//    «Ir para…» recentra-a na data escolhida; nas bordas, ver os meses anteriores/seguintes; «Hoje»
//    volta. 📌 Como um mapa de estradas no telemóvel: não se descarrega o país inteiro, vai-se
//    buscar a zona para onde se arrasta — e o pedaço tem sempre o mesmo tamanho (19 meses).
//
// ⚠️ Este componente só MOSTRA e PEDE: quem decide se a data já está na fita (e basta deslocar o
//    mapa) ou se é preciso pedir outra fita ao servidor é o `Mapa` (`irParaData`, `deslocarFita`).
// ============================================================================

import { useState } from 'react';
import {
  dentroDaJanela,
  fimDaJanela,
  indiceDaData,
  inicioDaFitaCentradaEm,
  inicioDeslocado,
  type JanelaDoMapa,
} from '@xlv-gva-mpt/dominio';
import { IconeChevron } from './icones.js';
import { useSaidaDaCaixa } from './saida-da-caixa.js';

/** Os meses que «anteriores» e «seguintes» deslocam a fita. */
export const MESES_POR_PASSO = 6;

const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

/**
 * A decisão do «Ir para…», pura: dentro da fita basta deslocar o mapa até ao dia; fora dela pede-se
 * a fita centrada nessa data. ⚠️ Nunca se recarrega o que já está à vista.
 */
export function decidirIrPara(
  janela: JanelaDoMapa,
  isoData: string,
): { readonly tipo: 'deslocar'; readonly dia: number } | { readonly tipo: 'recentrar'; readonly inicio: string } {
  const dia = indiceDaData(janela, isoData);
  return dentroDaJanela(janela, dia) ? { tipo: 'deslocar', dia } : { tipo: 'recentrar', inicio: inicioDaFitaCentradaEm(isoData) };
}

/**
 * As datas que «Ir para» aceita: a fita centrada nelas começa 3 meses antes, e o servidor só serve
 * fitas que começam entre 2000 e dez anos à frente (`fitaPedida`). Logo de 1 de Abril de 2000 ao fim
 * do ano daqui a dez.
 */
export function limitesDoIrPara(agora: Date): { readonly min: string; readonly max: string } {
  return { min: '2000-04-01', max: `${String(agora.getUTCFullYear() + 10)}-12-31` };
}

/** `2026-07-01` → `Jul 2026` (curto, porque vive na barra do topo). */
export function mesEAno(isoData: string): string {
  return `${MESES[Number(isoData.slice(5, 7)) - 1] ?? ''} ${isoData.slice(0, 4)}`;
}

/** Os anos que o selector oferece: os de `limitesDoIrPara`. */
function anosDoIrPara(agora: Date): readonly number[] {
  const { min, max } = limitesDoIrPara(agora);
  const anos: number[] = [];
  for (let a = Number(min.slice(0, 4)); a <= Number(max.slice(0, 4)); a++) anos.push(a);
  return anos;
}

/**
 * 📗 Cliente, 2026-10-26, ao ver a primeira versão: *«a interface ficou muito má… verificar se faz
 * sentido o seletor de forma tão exposta»*. Eram seis controlos na barra (◀, período, ▶, «Ir para»,
 * data, «Ir») para um gesto de poucas vezes por dia. Passa a ser **um botão** com o período, que
 * abre um painel com os passos de 6 meses e o «Ir para» por **mês e ano** — um planeador pensa em
 * meses, e o campo de data do navegador («dd/mm/aaaa») não.
 */
export function NavegarNaFita({
  janela,
  irParaData,
  deslocarFita,
  aCarregar = false,
}: {
  readonly janela: JanelaDoMapa;
  /** Leva o mapa à data — dentro da fita desloca-se; fora dela, pede a fita centrada nessa data. */
  readonly irParaData: (isoData: string) => void;
  /** Desloca a fita `meses` meses (negativo = para trás). */
  readonly deslocarFita: (meses: number) => void;
  /** Uma fita nova está a caminho: os botões esperam, para não se pedirem duas por cima. */
  readonly aCarregar?: boolean;
}) {
  const [aberto, definirAberto] = useState(false);
  // O mês proposto ao abrir é o «centro» da fita: 3 meses depois do início, que é onde a abertura
  // põe hoje (`inicioDaFitaCentradaEm`).
  const centro = inicioDeslocado(janela.inicio, 3);
  const [mes, definirMes] = useState(Number(centro.slice(5, 7)));
  const [ano, definirAno] = useState(Number(centro.slice(0, 4)));
  const saida = useSaidaDaCaixa(() => definirAberto(false));
  const agora = new Date();
  const { min, max } = limitesDoIrPara(agora);
  const alvo = `${String(ano)}-${String(mes).padStart(2, '0')}-01`;
  // ⚠️ Os limites do servidor (`fitaPedida`): fora deles não se pede nada — recusaria com 400.
  const valida = alvo >= min && alvo <= max;
  const periodo = `${mesEAno(janela.inicio)} – ${mesEAno(fimDaJanela(janela))}`;
  return (
    <span className="fita-nav" ref={saida.ref} onKeyDown={saida.onKeyDown}>
      <button
        type="button"
        className={aberto ? 'btn btn--peq btn--on' : 'btn btn--peq'}
        onClick={() => definirAberto((a) => !a)}
        aria-expanded={aberto}
        title="O período carregado no mapa — mudar"
      >
        <span>{periodo}</span>
        <IconeChevron em={0.9} aberto={aberto} className="ic ic--fraco" />
      </button>
      {aberto ? (
        <div className="fita-nav-painel painel-da-barra" role="dialog" aria-label="Período no mapa">
          <div className="fita-nav-titulo">Período no mapa</div>
          <div className="fita-nav-periodo">{periodo}</div>
          <div className="fita-nav-passos">
            <button
              type="button"
              className="btn btn--peq"
              onClick={() => deslocarFita(-MESES_POR_PASSO)}
              disabled={aCarregar}
              aria-label={`Ver os ${String(MESES_POR_PASSO)} meses anteriores`}
            >
              ◀ {MESES_POR_PASSO} meses
            </button>
            <button
              type="button"
              className="btn btn--peq"
              onClick={() => deslocarFita(MESES_POR_PASSO)}
              disabled={aCarregar}
              aria-label={`Ver os ${String(MESES_POR_PASSO)} meses seguintes`}
            >
              {MESES_POR_PASSO} meses ▶
            </button>
          </div>
          <div className="fita-nav-ir">
            <span>Ir para</span>
            <select value={mes} onChange={(e) => definirMes(Number(e.target.value))} aria-label="Mês">
              {MESES.map((m, i) => (
                <option key={m} value={i + 1}>
                  {m}
                </option>
              ))}
            </select>
            <select value={ano} onChange={(e) => definirAno(Number(e.target.value))} aria-label="Ano">
              {anosDoIrPara(agora).map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn--peq btn--destaque"
              disabled={aCarregar || !valida}
              onClick={() => {
                irParaData(alvo);
                definirAberto(false);
              }}
            >
              Ir
            </button>
          </div>
        </div>
      ) : null}
    </span>
  );
}
